import React from 'react';
import { EngineOutput } from '../model/model';
import FormBox from './subcomponents/FormBox';
import { styled } from 'styled-components';
import PhaseOutput from './subcomponents/PhaseOutput';

const OutputContainer = styled.div<{ $isSmallScreen: boolean }>`
width: ${({ $isSmallScreen }) => ($isSmallScreen ? '80vw' : '64vw')};
`

const SummaryContainer = styled.div`
    display: flex;
    flex-direction: row;
`;

const DetailsContainer = styled.div`
    width: 70%;
    padding: 1rem;
    padding-left: 0.5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const SumContainer = styled.div`
    width: 30%;
    padding: 1rem;
    padding-right: 0.5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const Title = styled.span`
    text-align: center;
    font-size: 1rem;
    font-weight: 500;
    margin-bottom: 1rem;
`

const SumPhaseContainer = styled.div`
    display: flex;
    &:not(:last-child) {
        border-bottom: 1px solid #58585A; /* Horizontal line for each row */
    }
`;

const SumPhaseItem = styled.span<{ $isBottomItem: boolean }>`
    flex: 1; /* Each item takes equal space */
    text-align: center;
    padding: 1rem;
    font-size: 0.9rem;
    &:not(:last-child) {
        border-right: 1px solid #58585A; // Adds vertical border
    }
    background-color: ${({ $isBottomItem }) => $isBottomItem ? 'linear-gradient(to right, #3B56A6, #2081C4)' : '#FFFFFF'};
    border-radius: ${({ $isBottomItem }) => $isBottomItem ? '0px 0px 0px 27px' : '0'};
`;

const SumTypography = styled(SumPhaseItem)`
    color: #ffffff;
    font-weight: 500;
    background-image: linear-gradient(to right, #3B56A6, #2081C4);
    border-radius: 0px 0px 0px 27px;
`;

const HeaderContainer = styled.div`
    display: flex;
    flex-grow: 1;
    background-color: #E9ECF6;
    border-radius: 30px 30px 0 0;
    overflow: hidden; // This ensures that the border-radius is applied to the child elements as well
`;

const HeaderItem = styled.span`
    flex: 1;
    text-align: center;
    font-size: 0.9rem;
    font-weight: 500;
    padding: 0.5rem;
    &:not(:last-child) {
        border-right: 1px solid #58585A; // Adds vertical border
    }
    &:first-child {
        border-radius: 30px 0 0 0; // Round the top-left corner
    }
    &:last-child {
        border-radius: 0 30px 0 0; // Round the top-right corner
    }
`;

const SumHeader = (
    <HeaderContainer>
        <HeaderItem>FAZA <br /> SPRZEDAŻY</HeaderItem>
        <HeaderItem>ZYSK RAZEM <br />(SMART)</HeaderItem>
        <HeaderItem>ZYSK RAZEM <br /> (NON-SMART)</HeaderItem>
    </HeaderContainer>
);

const DetailsHeader = (
    <HeaderContainer>
        <HeaderItem>FAZA <br /> SPRZEDAŻY</HeaderItem>
        <HeaderItem>ZYSK (NETTO) NA PRODUKCIE <br />(SMART)</HeaderItem>
        <HeaderItem>ZYSK (NETTO) NA PRODUKCIE <br /> (NON-SMART)</HeaderItem>
    </HeaderContainer>
);

interface SummaryProps {
    engineOutput: EngineOutput[];
    isSmallScreen: boolean;
}

const Summary: React.FC<SummaryProps> = ({ engineOutput, isSmallScreen }) => {

    const profitSmartSum = engineOutput.reduce((acc, item) => acc + item.phaseProductNetSmartProfitTotal, 0).toFixed(2);
    const profitNonSmartSum = engineOutput.reduce((acc, item) => acc + item.phaseProductNetNonSmartProfitTotal, 0).toFixed(2);

    return (
        <OutputContainer $isSmallScreen={isSmallScreen}>
            <FormBox variant='gradient' headerVariant='title' headerTitle="PODSUMOWANIE KOSZTÓW I ZYSKÓW" width='100%'>
                <SummaryContainer>
                    <SumContainer>
                        <Title>RAZEM</Title>
                        <FormBox variant='standard' headerVariant='component' headerComponent={SumHeader} width='100%'>
                            {engineOutput.map((item, index) => (
                                <SumPhaseContainer key={index}>
                                    <SumPhaseItem $isBottomItem={false}>{index + 1}</SumPhaseItem>
                                    <SumPhaseItem $isBottomItem={false}>{item.phaseProductNetSmartProfitTotal.toFixed(2)} zł</SumPhaseItem>
                                    <SumPhaseItem $isBottomItem={false}>{item.phaseProductNetNonSmartProfitTotal.toFixed(2)} zł</SumPhaseItem>
                                </SumPhaseContainer>
                            ))}
                            <SumPhaseContainer >
                                <SumTypography $isBottomItem={true}>RAZEM</SumTypography>
                                <SumPhaseItem $isBottomItem={true}>{profitSmartSum} zł</SumPhaseItem>
                                <SumPhaseItem $isBottomItem={true}>{profitNonSmartSum} zł</SumPhaseItem>
                            </SumPhaseContainer>
                        </FormBox >
                    </SumContainer>
                    <DetailsContainer>
                        <Title>DANE OGÓLNE NA PRODUKCIE</Title>
                        <FormBox variant='standard' headerVariant='component' headerComponent={DetailsHeader} width='100%'>
                            {engineOutput.map((item, index) => (
                                <div key={index + 1} >
                                    <PhaseOutput phaseNumber={index + 1} engineOutput={item} isLastPhase={index + 1 === engineOutput.length} />
                                </div>
                            ))}
                        </FormBox >
                    </DetailsContainer>
                </SummaryContainer>
            </FormBox>
        </OutputContainer>
    );
};

export default Summary;